import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';

const Panel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.015), transparent 35%),
    var(--color-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 1.1rem 1.15rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 0.85rem;
  padding: 0 0 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg { color: ${accent}; }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 0.7rem;
  padding: 0.55rem 0;
  border-left: 2px solid rgba(0, 180, 216, 0.35);
  padding-left: 0.7rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 0.65rem;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.15);
  }
`;

const DateCell = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${accent};
  white-space: nowrap;
`;

const Description = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

const CtaButton = styled.button`
  margin-top: 0.8rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 0.85rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  font-family: inherit;
  transition: color 0.18s ease, border-color 0.18s ease;

  svg { font-size: 0.7rem; transition: transform 0.18s ease; }

  &:hover {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.35);
    svg { transform: translateX(3px); }
  }
`;

const EventsCard = ({ events = [] }) => {
  const navigate = useNavigate();
  return (
    <Panel>
      <Header>
        <Title>
          <FaCalendarAlt /> Próximos eventos
        </Title>
      </Header>

      {events.length > 0 ? (
        <List>
          {events.map((event, i) => (
            <Row key={i}>
              <DateCell>{event.date}</DateCell>
              <Description>{event.description}</Description>
            </Row>
          ))}
        </List>
      ) : (
        <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          La agenda y el roadmap de campaña por horizontes se gestionan en <b>Planificación</b>.
        </p>
      )}

      <CtaButton type="button" onClick={() => navigate('/planning')}>
        Abrir Planificación <FaArrowRight />
      </CtaButton>
    </Panel>
  );
};

export default EventsCard;
